import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import AdminDashboard from "../AdminDashboard/admindash";
import ConfirmModal from "../AdminDashboard/ConfirmModal";
import {
  PenTool,
  Trash2,
  Edit3,
  Search,
  Filter,
  FileText,
  User,
  Tag,
  Calendar,
  Clock,
  TrendingUp,
  Loader2,
  Plus,
  ArrowUpRight
} from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";


const BlogAdminView = () => {
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // Deletion Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);

  // Fetch all blogs
  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/blogs`);
      if (response.data && response.data.blogs) {
        setBlogs(response.data.blogs);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to synchronize blog registry.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (blog) => {
    setBlogToDelete(blog);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!blogToDelete) return;
    try {
      await axios.delete(`${API_URL}/blogs/${blogToDelete._id}`);
      setBlogs(blogs.filter((b) => b._id !== blogToDelete._id));
      toast.success(`"${blogToDelete.title}" has been permanently purged.`);
    } catch (err) {
      console.error(err);
      toast.error("Security protocols blocked the deletion of this story.");
    } finally {
      setBlogToDelete(null);
    }
  };

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "All" || blog.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getTopCategory = () => {
    if (blogs.length === 0) return "N/A";
    const counts = blogs.reduce((acc, blog) => {
      acc[blog.category] = (acc[blog.category] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  };

  const stats = [
    { label: "Total Stories", value: blogs.length, icon: FileText, color: "text-sky-600", bg: "bg-sky-50" },
    { label: "Top Category", value: getTopCategory(), icon: Tag, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Elite Authors", value: [...new Set(blogs.map(b => b.author))].length, icon: User, color: "text-indigo-600", bg: "bg-indigo-50" },
    { label: "Reader Reach", value: "Premium", icon: TrendingUp, color: "text-amber-600", bg: "bg-amber-50" },
  ];

  return (
    <AdminDashboard>
      <div className="max-w-7xl mx-auto space-y-10 font-sans">

        {/* Header Area */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 border-b border-gray-100 pb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-50 text-sky-600 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
              <PenTool size={12} /> Narrative Management
            </div>
            <h1 className="text-4xl font-black text-sky-950 tracking-tight leading-none">
              Editorial <span className="text-sky-600 italic">Control Center</span>
            </h1>
            <p className="text-gray-400 font-bold text-sm mt-3 max-w-lg">Manage, refine, and publish high-impact stories across the TravelAura network.</p>
          </div>
          <Link
            to="/addblog"
            className="group flex items-center gap-3 bg-sky-950 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-sky-800 transition-all shadow-xl shadow-sky-950/20 active:scale-95"
          >
            <Plus size={18} className="group-hover:rotate-90 transition-transform duration-500" />
            Compose New Story
          </Link>
        </div>

        {/* Real-time Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-md transition-all flex items-center gap-5 group">
              <div className={`w-14 h-14 ${stat.bg} ${stat.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                <stat.icon size={24} />
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">{stat.label}</p>
                <h4 className="text-2xl font-black text-sky-950 tracking-tight">{stat.value}</h4>
              </div>
            </div>
          ))}
        </div>

        {/* Discovery & Filters */}
        <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col lg:flex-row gap-6 items-center">
          <div className="flex-grow flex items-center gap-4 bg-gray-50 px-6 py-4 rounded-2xl w-full border border-transparent focus-within:border-sky-300 transition-all">
            <Search className="text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by story title or editorial contact..."
              className="bg-transparent border-none outline-none w-full font-bold text-sky-950 placeholder:text-gray-300"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center gap-2 bg-gray-50 px-6 py-4 rounded-2xl border border-transparent">
              <Filter className="text-gray-400" size={18} />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-transparent border-none outline-none font-black text-[11px] uppercase tracking-widest text-sky-900 cursor-pointer"
              >
                <option value="All">All Narratives</option>
                <option value="Travel">Travel</option>
                <option value="Adventure">Adventure</option>
                <option value="Events">Events</option>
                <option value="Food">Food</option>
              </select>
            </div>

            <button
              onClick={fetchBlogs}
              className="p-4 bg-sky-50 text-sky-600 rounded-2xl hover:bg-sky-100 transition-colors"
              title="Synchronize Data"
            >
              <Loader2 className={`${isLoading ? 'animate-spin' : ''}`} size={20} />
            </button>
          </div>
        </div>

        {/* Assets Registry */}
        <div className="bg-white rounded-[3rem] border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-10 py-8 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Story Identity</th>
                  <th className="px-10 py-8 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Editorial Context</th>
                  <th className="px-10 py-8 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Flow Control</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  <tr>
                    <td colSpan="3" className="px-10 py-32 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <Loader2 className="animate-spin text-sky-300" size={48} />
                        <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Compiling Narrative Registry...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredBlogs.length > 0 ? (
                  filteredBlogs.map((blog, idx) => (
                    <tr key={blog._id} className="hover:bg-sky-50/20 transition-all duration-300 group">
                      <td className="px-10 py-8">
                        <div className="flex items-center gap-6">
                          <div className="relative w-20 h-20 rounded-2xl overflow-hidden shadow-inner flex-shrink-0 group-hover:scale-110 transition-transform duration-500">
                            <img
                              src={blog.coverImageUrl || "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1000&auto=format&fit=crop"}
                              alt={blog.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                          </div>
                          <div className="max-w-md">
                            <h3 className="font-black text-sky-950 text-lg tracking-tight mb-2 group-hover:text-sky-600 transition-colors">{blog.title}</h3>
                            <div className="flex items-center gap-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                              <span className="flex items-center gap-1.5"><Tag size={12} className="text-sky-300" /> {blog.category}</span>
                              <span className="flex items-center gap-1.5"><User size={12} className="text-indigo-300" /> {blog.author}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-10 py-8">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            <Calendar size={12} className="text-emerald-400" /> Published {new Date(blog.createdAt || Date.now()).toLocaleDateString()}
                          </div>
                          <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            <Clock size={12} className="text-amber-400" /> Estimated 5 Min Read
                          </div>
                          <Link to={`/blog/${blog._id}`} className="inline-flex items-center gap-1.5 text-sky-600 font-black text-[9px] uppercase tracking-widest hover:translate-x-1 transition-transform">
                            Review Live Story <ArrowUpRight size={12} />
                          </Link>
                        </div>
                      </td>
                      <td className="px-10 py-8">
                        <div className="flex items-center gap-3">
                          <Link
                            to={`/update/${blog._id}`}
                            className="w-12 h-12 bg-white text-sky-600 border border-sky-100 rounded-2xl hover:bg-sky-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                            title="Refine Narrative"
                          >
                            <Edit3 size={20} />
                          </Link>
                          <button
                            onClick={() => handleDeleteClick(blog)}
                            className="w-12 h-12 bg-rose-50 text-rose-500 rounded-2xl hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center group/del"
                            title="Terminate Story"
                          >
                            <Trash2 size={20} className="group-hover/del:scale-110 transition-transform" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="px-10 py-32 text-center">
                      <div className="flex flex-col items-center gap-6">
                        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center text-gray-200 shadow-inner">
                          <FileText size={48} />
                        </div>
                        <div className="max-w-xs mx-auto">
                          <h3 className="text-xl font-black text-sky-950 italic">Registry Silent</h3>
                          <p className="text-xs text-gray-400 font-bold mt-2">No active narratives matches your editorial search parameters.</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Deletion Confirmation */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          title="Narrative Purge Authorization"
          message={`This will permanently destroy the story "${blogToDelete?.title}" and erase its editorial history from the TravelAura archives. Proceed with terminal revocation?`}
          confirmText="Purge Story"
        />
      </div>
    </AdminDashboard>
  );
};

export default BlogAdminView;
